/**
 * FormVersionSelector Component
 *
 * A component for managing form versions including:
 * - Displaying current version information
 * - Creating new versions
 * - Viewing version history
 * - Switching between versions
 */

import React, { useState, useEffect } from 'react';
import {
    Button,
    Modal,
    Select,
    Table,
    Tag,
    Tooltip,
    Space,
    message,
    Card,
    Typography,
    Divider
} from 'antd';
import {
    BranchesOutlined,
    HistoryOutlined,
    TagOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    PlayCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import formTemplateService from '../../services/formTemplateService';

const { Option } = Select;
const { Title, Text } = Typography;

const FormVersionSelector = ({ formId, currentVersion, onVersionChange, onVersionSelect, disabled = false }) => {
    const [versions, setVersions] = useState([]);
    const [versionInfo, setVersionInfo] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(currentVersion);
    const [loading, setLoading] = useState(false);
    const [versionHistoryModalVisible, setVersionHistoryModalVisible] = useState(false);
    const [activatingVersion, setActivatingVersion] = useState(null);

    // Load version information on component mount and when formId changes
    useEffect(() => {
        if (formId) {
            loadVersionInfo();
        }
    }, [formId]);

    // Update selected version when currentVersion prop changes
    useEffect(() => {
        setSelectedVersion(currentVersion);
    }, [currentVersion]);

    const loadVersionInfo = async () => {
        setLoading(true);
        try {
            const result = await formTemplateService.getFormVersionInfo(formId);
            if (result.success) {
                setVersionInfo(result.data);

                // Load detailed versions list
                const versionsResult = await formTemplateService.getFormVersions(formId);
                if (versionsResult.success) {
                    const versionsList = versionsResult.data.items || [];
                    setVersions(versionsList);
                }
            } else {
                message.error('Failed to load version information');
            }
        } catch (error) {
            console.error('Error loading version info:', error);
            message.error('Error loading version information');
        } finally {
            setLoading(false);
        }
    };

    const handleVersionSelect = (version) => {
        setSelectedVersion(version);
        if (onVersionSelect) {
            onVersionSelect(formId, version);
        }
    };

    const handleActivateVersion = async (version) => {
        Modal.confirm({
            title: 'Activate Form Version',
            content: `Are you sure you want to make version ${version} active for nurses? This will change which version nurses see in the clinical forms.`,
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes, Activate',
            cancelText: 'Cancel',
            onOk: async () => {
                setActivatingVersion(version);
                try {
                    const result = await formTemplateService.activateFormVersion(formId, version);
                    if (result.success) {
                        message.success(`Version ${version} activated successfully`);
                        // Reload version info to update the UI
                        await loadVersionInfo();
                        // Notify parent component about version change
                        if (onVersionChange) {
                            onVersionChange(result.data);
                        }
                    } else {
                        message.error(result.error || 'Failed to activate version');
                    }
                } catch (error) {
                    console.error('Error activating version:', error);
                    message.error('Error activating version');
                } finally {
                    setActivatingVersion(null);
                }
            }
        });
    };

    const getVersionColumns = () => [
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            render: (version, record) => (
                <Space>
                    <Tag
                        color={record.is_latest_version ? 'green' : 'default'}
                        style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            fontSize: '0.875em',
                            fontWeight: '500'
                        }}
                    >
                        {version}
                    </Tag>
                    {record.is_latest_version && (
                        <Tag color="blue" icon={<CheckCircleOutlined />}>
                            Active
                        </Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Notes',
            dataIndex: 'version_notes',
            key: 'version_notes',
            render: (notes) => notes || <Text type="secondary">No notes</Text>
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString('en-GB', { hour12: true })
        },
        {
            title: 'Parent',
            dataIndex: 'parent_form_version',
            key: 'parent_form_version',
            render: (parent) => parent || <Text type="secondary">Initial version</Text>
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    {!record.is_latest_version && (
                        <Tooltip title="Make this version active for nurses">
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlayCircleOutlined />}
                                loading={activatingVersion === record.version}
                                onClick={() => handleActivateVersion(record.version)}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Make Active
                            </Button>
                        </Tooltip>
                    )}
                    {record.is_latest_version && (
                        <Tag color="blue" icon={<CheckCircleOutlined />}>
                            Currently Active
                        </Tag>
                    )}
                </Space>
            )
        }
    ];

    if (!versionInfo) {
        return null;
    }

    return (
        <Card
            size="small"
            title={
                <Space>
                    <BranchesOutlined />
                    Form Versions
                </Space>
            }
            extra={
                <Tooltip title="View version history">
                    <Button
                        icon={<HistoryOutlined />}
                        size="small"
                        onClick={() => setVersionHistoryModalVisible(true)}
                    >
                        History ({versionInfo.total_versions})
                    </Button>
                </Tooltip>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                        <Text strong>Current Version: </Text>
                        <Tag color="green" icon={<TagOutlined />}>
                            {selectedVersion || versionInfo.latest_version}
                        </Tag>
                    </div>

                    {versions.length > 1 && !disabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text>Edit Version: </Text>
                            <Select
                                value={selectedVersion || versionInfo.latest_version}
                                style={{ minWidth: 120 }}
                                size="small"
                                onChange={handleVersionSelect}
                                placeholder="Select version"
                                dropdownStyle={{ zIndex: 1090 }}
                            >
                                {versions
                                    .sort((a, b) => {
                                        // Sort by version number (v1, v2, v3...) - latest first
                                        const aNum = parseInt(a.version.substring(1)) || 0;
                                        const bNum = parseInt(b.version.substring(1)) || 0;
                                        return bNum - aNum;
                                    })
                                    .map((version) => (
                                    <Option key={version.version} value={version.version}>
                                        <Space>
                                            {version.version}
                                            {version.is_latest_version && (
                                                <Tag color="blue" size="small">Active</Tag>
                                            )}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {versions.length > 1 && !disabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text>Make Active: </Text>
                            <Select
                                value={versionInfo.latest_version}
                                style={{ minWidth: 120 }}
                                size="small"
                                onChange={(version) => handleActivateVersion(version)}
                                placeholder="Select version to activate"
                                dropdownStyle={{ zIndex: 1090 }}
                                loading={activatingVersion !== null}
                            >
                                {versions
                                    .sort((a, b) => {
                                        // Sort by version number (v1, v2, v3...) - latest first
                                        const aNum = parseInt(a.version.substring(1)) || 0;
                                        const bNum = parseInt(b.version.substring(1)) || 0;
                                        return bNum - aNum;
                                    })
                                    .map((version) => (
                                    <Option key={version.version} value={version.version}>
                                        <Space>
                                            {version.version}
                                            {version.is_latest_version && (
                                                <Tag color="blue" size="small">Currently Active</Tag>
                                            )}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    <Tooltip title="This form has multiple versions">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                </div>

                <Text type="secondary" style={{ fontSize: '12px' }}>
                    Total versions: {versionInfo.total_versions} |
                    Active version for nurses: {versionInfo.latest_version}
                    {selectedVersion && selectedVersion !== versionInfo.latest_version && (
                        <span style={{ color: '#ff7a00', fontWeight: 'bold' }}>
                            {' '} | Currently editing version {selectedVersion}
                        </span>
                    )}
                </Text>
            </Space>

            {/* Version History Modal */}
            <Modal
                title="Form Version History"
                open={versionHistoryModalVisible}
                onCancel={() => setVersionHistoryModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setVersionHistoryModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={1000}
                zIndex={1070}
            >
                <Table
                    dataSource={versions.sort((a, b) => {
                        // Sort by version number (v1, v2, v3...) - latest first
                        const aNum = parseInt(a.version.substring(1)) || 0;
                        const bNum = parseInt(b.version.substring(1)) || 0;
                        return bNum - aNum;
                    })}
                    columns={getVersionColumns()}
                    rowKey="version"
                    size="small"
                    pagination={false}
                    loading={loading}
                    scroll={{ x: 800 }}
                />
            </Modal>
        </Card>
    );
};

export default FormVersionSelector;